/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  Active,
  closestCenter,
  CollisionDetection,
  DragOverlay,
  DndContext,
  DropAnimation,
  KeyboardCoordinateGetter,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  useSortable,
  SortableContext,
  rectSortingStrategy,
  AnimateLayoutChanges,
  NewIndexGetter,
} from '@dnd-kit/sortable';
import { Item } from './Item';
import { CardWidgetCard } from '../../../types';
import { findIndex } from 'lodash';
import { useGuideStore } from '../../../stores/guideStore';

interface Props {
  widgetStyle: string;
  isActiveWidget: boolean;
  collisionDetection?: CollisionDetection;
  coordinateGetter?: KeyboardCoordinateGetter;
  dropAnimation?: DropAnimation | null;
  getNewIndex?: NewIndexGetter;
  handle?: boolean;
  handleDragEnd: (from: number, to: number) => void;
  Container: (props: any) => React.ReactElement;
  OffMapItem?: (props: any) => React.ReactElement;
  items?: CardWidgetCard[];
  removable?: boolean;
  reorderItems?: typeof arrayMove;
  useDragOverlay?: boolean;
  getItemStyles?(args: {
    id: UniqueIdentifier;
    index: number;
    isSorting: boolean;
    isDragOverlay: boolean;
    overIndex: number;
    isDragging: boolean;
  }): React.CSSProperties;
  wrapperStyle?(args: {
    active: Pick<Active, 'id'> | null;
    index: number;
    isDragging: boolean;
    id: UniqueIdentifier;
  }): React.CSSProperties;
  isDisabled?(id: UniqueIdentifier): boolean;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

export function Sortable({
  widgetStyle,
  collisionDetection = closestCenter,
  dropAnimation = dropAnimationConfig,
  isActiveWidget,
  getItemStyles = () => ({}),
  getNewIndex,
  Container,
  handleDragEnd,
  OffMapItem,
  handle = true,
  items: initialItems,
  isDisabled = () => false,
  removable,
  reorderItems = arrayMove,
  useDragOverlay = true,
  wrapperStyle = () => ({}),
}: Props) {
  const [items, setItems] = useState<CardWidgetCard[]>(() => initialItems);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const isFirstAnnouncement = useRef(true);
  const getIndex = (id: UniqueIdentifier) =>
    findIndex(items, item => item.id === id);
  const activeIndex = activeId ? getIndex(activeId) : -1;
  const handleRemove = removable
    ? (id: UniqueIdentifier) =>
        setItems(items => items.filter(item => item.id !== id))
    : undefined;

  const isEditMode = useGuideStore(state => state.isEditMode);
  const isWidgetActiveAndEditMode = isEditMode && isActiveWidget;

  useEffect(() => {
    if (!activeId) {
      isFirstAnnouncement.current = true;
    }
  }, [activeId]);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          handleDragEnd(activeIndex, getIndex(over.id));
          const overIndex = getIndex(over.id);
          if (activeIndex !== overIndex) {
            setItems(items => reorderItems(items, activeIndex, overIndex));
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <Container>
          {items.map((item, index) => (
            <SortableItem
              widgetStyle={widgetStyle}
              isWidgetActive={isWidgetActiveAndEditMode}
              item={item}
              key={item.id}
              id={item.id}
              handle={handle}
              index={index}
              style={getItemStyles}
              wrapperStyle={wrapperStyle}
              disabled={isDisabled(item.id)}
              onRemove={handleRemove}
              useDragOverlay={useDragOverlay}
              getNewIndex={getNewIndex}
            />
          ))}
          {OffMapItem && <OffMapItem />}
        </Container>
      </SortableContext>
      {useDragOverlay
        ? createPortal(
            <DragOverlay adjustScale dropAnimation={dropAnimation}>
              {activeId ? (
                <Container>
                  <Item
                    widgetStyle={widgetStyle}
                    card={items[activeIndex]}
                    value={items[activeIndex].id}
                    handle={handle}
                    wrapperStyle={wrapperStyle({
                      active: { id: activeId },
                      index: activeIndex,
                      isDragging: true,
                      id: items[activeIndex].id,
                    })}
                    style={getItemStyles({
                      id: items[activeIndex].id,
                      index: activeIndex,
                      isSorting: activeId !== null,
                      isDragging: true,
                      overIndex: -1,
                      isDragOverlay: true,
                    })}
                    dragOverlay
                  />
                </Container>
              ) : null}
            </DragOverlay>,
            document.body,
          )
        : null}
    </DndContext>
  );
}

interface SortableItemProps {
  item: CardWidgetCard;
  widgetStyle: string;
  animateLayoutChanges?: AnimateLayoutChanges;
  disabled?: boolean;
  getNewIndex?: NewIndexGetter;
  id: UniqueIdentifier;
  index: number;
  handle: boolean;
  useDragOverlay?: boolean;
  onRemove?(id: UniqueIdentifier): void;
  style(values: any): React.CSSProperties;
  renderItem?(args: any): React.ReactElement;
  wrapperStyle: Props['wrapperStyle'];
  isWidgetActive: boolean;
}

function SortableItem({
  item,
  widgetStyle,
  disabled,
  getNewIndex,
  handle,
  id,
  index,
  onRemove,
  style,
  renderItem,
  useDragOverlay,
  wrapperStyle,
  isWidgetActive,
}: SortableItemProps) {
  const {
    active,
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    disabled,
    getNewIndex,
  });

  return (
    <Item
      widgetStyle={widgetStyle}
      isWidgetActive={isWidgetActive}
      card={item}
      ref={setNodeRef}
      value={id}
      disabled={disabled}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={
        handle
          ? {
              ref: setActivatorNodeRef,
            }
          : undefined
      }
      renderItem={renderItem}
      index={index}
      style={style({
        index,
        id,
        isDragging,
        isSorting,
        overIndex,
      })}
      onRemove={onRemove ? () => onRemove(id) : undefined}
      transform={transform}
      transition={transition}
      wrapperStyle={wrapperStyle?.({ index, isDragging, active, id })}
      listeners={listeners}
      data-index={index}
      data-id={id}
      dragOverlay={!useDragOverlay && isDragging}
      {...attributes}
    />
  );
}
